import React from 'react';
import { Box, Button, Typography, Alert } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';

interface ApiClientDangerZoneProps {
  title: string;
  description: string;
  buttonLabel: string;
  buttonColor: 'error' | 'warning';
  isActive?: boolean;
  onAction: () => Promise<void>;
  isLoading?: boolean;
}

export const ApiClientDangerZone: React.FC<ApiClientDangerZoneProps> = ({
  title,
  description,
  buttonLabel,
  buttonColor,
  isActive = true,
  onAction,
  isLoading = false,
}) => {
  const [confirming, setConfirming] = React.useState(false);

  const handleAction = async () => {
    if (confirming) {
      setConfirming(false);
      await onAction();
    } else {
      setConfirming(true);
    }
  };

  const handleCancel = () => {
    setConfirming(false);
  };

  const borderColor = buttonColor === 'warning' ? '#ed6c02' : 'error.main';

  return (
    <Box
      sx={{
        mt: 4,
        p: 3,
        border: 2,
        borderColor,
        borderRadius: 2,
        backgroundColor: 'error.lighter',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <WarningIcon sx={{ color: borderColor }} />
        <Typography variant="h6" sx={{ color: borderColor, fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>

      <Alert severity={buttonColor} sx={{ mb: 2 }}>
        {description}
      </Alert>

      {confirming ? (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Are you sure?
          </Typography>
          <Button
            variant="contained"
            color={buttonColor}
            onClick={handleAction}
            disabled={isLoading || !isActive}
            size="small"
          >
            {isLoading ? 'Processing...' : 'Confirm'}
          </Button>
          <Button variant="outlined" onClick={handleCancel} size="small" disabled={isLoading}>
            Cancel
          </Button>
        </Box>
      ) : (
        <Button
          variant={buttonColor === 'warning' ? 'outlined' : 'contained'}
          color={buttonColor}
          onClick={handleAction}
          disabled={isLoading || !isActive}
          sx={{ minWidth: 200 }}
        >
          {isLoading ? 'Processing...' : buttonLabel}
        </Button>
      )}

      {!isActive && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          This client is already revoked.
        </Typography>
      )}
    </Box>
  );
};
